import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ConnectedFacility,
  ConnectionPresentation,
  ConnectionsService,
} from '../../../../../js/api';
import { ConnectionRateInformation } from '../../../components/connection-rate-information';
import { DocumentActionDialogs, useDocumentActions } from '../../../components/document-actions';
import { DocumentLink } from '../../../components/document-type-access';
import { DOCUMENTS_QUERY_KEY } from '../../../components/documents-tab';
import { DrawerSectionHeader, DrawerField } from '../../../components/drawer-components';
import { MalpracticeHistorySection } from '../../../components/malpractice-history-section';
import { Spinner } from '../../../components/spinner';
import { useHasPermission } from '../../../hooks/useHasPermission';
import { formatApiDate } from '../../../utils/date';
import { SIGNATURE_REQUEST_UPDATE_PERMISSION } from '../../../utils/permissions';
import { useAudience } from '../../../utils/workflowAccess';
import { PresentationDownloads } from './PresentationDownloads';
import { CONNECTION_QUERY_KEY } from './queryKeys';

type PresentationInfoTabProps = {
  connection: ConnectedFacility;
};

export const presentationQueryKey = (connectionId: string) => [
  CONNECTION_QUERY_KEY,
  connectionId,
  'presentation',
];

const sectionIcon = (icon: string) => (
  <i className={`fas ${icon} tw:text-brand-green-0 tw:dark:text-brand-green-1 fa-fw`} />
);

const formatDate = (value: string | null | undefined) =>
  value ? formatApiDate(value, { dateFormat: 'MM/dd/yyyy' }) : '--';

/**
 * What the facility reviews before accepting a clinician (PEND_FAC_REV).
 *
 * Everything here comes from ONE payload (`GET .../presentation/`), the same payload
 * the printable presentation PDF is rendered from — so what the facility downloads
 * says exactly what this tab shows. Order: the connection's hourly rates, the CV, the
 * state license, the certifications and, last, malpractice & other history. The drawer
 * header already links to the clinician's profile, so there is no profile link here.
 */
// eslint-disable-next-line sonarjs/cognitive-complexity
export function PresentationInfoTab({ connection }: PresentationInfoTabProps) {
  const providerId = connection.provider?.id;
  const queryClient = useQueryClient();
  const audience = useAudience();
  const canUpdateSignature = useHasPermission(SIGNATURE_REQUEST_UPDATE_PERMISSION);

  const { data: presentation, isError } = useQuery<ConnectionPresentation>({
    queryKey: presentationQueryKey(connection.id),
    queryFn: () =>
      ConnectionsService.connectionsConnectedFacilityPresentationRetrieve({ id: connection.id }),
  });

  // The CV's view link + paperclip menu are the documents framework's own; a change
  // through them refreshes this payload (and the clinician's documents views).
  const documentActions = useDocumentActions({
    invalidate: () => {
      queryClient.invalidateQueries({ queryKey: presentationQueryKey(connection.id) });
      if (providerId)
        queryClient.invalidateQueries({ queryKey: [DOCUMENTS_QUERY_KEY, providerId] });
    },
  });
  const viewer = { ownerId: providerId ?? '', audience, canUpdateSignature };

  // The rates are the connection's own and show at once; the rest is the payload.
  const rates = <ConnectionRateInformation connection={connection} />;

  if (!presentation) {
    return (
      <div className="tw:space-y-4">
        {rates}
        <div className="tw:flex tw:items-center tw:py-4 tw:text-sm tw:text-zinc-500">
          {isError ? (
            'Unable to load the presentation.'
          ) : (
            <>
              <Spinner
                size="sm"
                variant="zinc"
                className="tw:mr-2"
                aria-label="Loading presentation"
              />
              Loading presentation...
            </>
          )}
        </div>
      </div>
    );
  }

  const { cv, credentials, malpractice } = presentation;
  const activeLicense = credentials?.active_state_license;

  return (
    <div className="tw:space-y-4">
      {/* Hourly rates: the same box as the Details tab */}
      {rates}

      <div data-testid="presentation-downloads">
        <PresentationDownloads
          connectionId={connection.id}
          cv={cv}
          onDownloadCv={() =>
            cv.document && documentActions.openDocument(cv.document.id, 'attachment')
          }
        />
      </div>

      {/* CV: the latest curriculum-vitae document on the clinician, else the legacy file */}
      <section>
        <DrawerSectionHeader text="CV" icon={sectionIcon('fa-file-lines')} />
        <div className="tw:text-sm tw:ml-5.75" data-testid="presentation-cv">
          {cv.document ? (
            <DocumentLink doc={cv.document} viewer={viewer} actions={documentActions} />
          ) : cv.legacy_url ? (
            <a
              href={cv.legacy_url}
              target="_blank"
              rel="noopener noreferrer"
              className="tw:text-brand-green-0 tw:hover:text-brand-green-1 tw:hover:underline"
              data-testid="presentation-legacy-cv"
            >
              <i className="fas fa-download tw:mr-2 fa-fw" />
              Download CV
            </a>
          ) : (
            <span className="tw:text-gray-500">No CV available</span>
          )}
        </div>
      </section>

      {/* Active State License */}
      <section>
        <DrawerSectionHeader text="State License" icon={sectionIcon('fa-id-card')} />
        <div className="tw:grid tw:grid-cols-[auto_1fr] tw:gap-x-6 tw:gap-y-2 tw:text-sm">
          <DrawerField label="IMLC" value={credentials?.has_imlc ? 'Yes' : 'No'} />
          {activeLicense && (
            <>
              <DrawerField label="State" value={activeLicense.state_title || activeLicense.state} />
              <DrawerField label="Number" value={activeLicense.number || '--'} />
              <DrawerField
                label="Expiration Date"
                value={formatDate(activeLicense.expiration_date)}
              />
            </>
          )}
        </div>
      </section>

      {/* Board Certifications */}
      {!!credentials?.board_certifications.length && (
        <section>
          <DrawerSectionHeader text="Board Certifications" icon={sectionIcon('fa-certificate')} />
          <div className="tw:space-y-2 tw:text-sm">
            {credentials.board_certifications.map((cert, index) => (
              <div key={`${cert.type}-${index}`} className="tw:flex tw:items-center tw:ml-5.75">
                <span>
                  {cert.title}
                  {cert.board_eligible ? ' - Board Eligible' : ''}
                </span>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Life Support Certifications */}
      {!!credentials?.certifications.length && (
        <section>
          <DrawerSectionHeader
            text="Life Support Certifications"
            icon={sectionIcon('fa-heartbeat')}
          />
          <div className="tw:space-y-2 tw:text-sm">
            {credentials.certifications.map((cert, index) => (
              <div key={`${cert.type}-${index}`} className="tw:flex tw:items-center tw:ml-5.75">
                <span>
                  {cert.title}
                  {cert.expiration_date ? ` - Expires: ${formatDate(cert.expiration_date)}` : ''}
                </span>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Malpractice & Other History (the payload's summary + the View dialog) — last */}
      {providerId && <MalpracticeHistorySection providerId={providerId} summary={malpractice} />}

      <DocumentActionDialogs actions={documentActions} audience={audience} />
    </div>
  );
}
